import { equal, handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import {
  LandingPageTemplateFormState,
  PageTemplatesEnum,
} from '@/client/page-templates';
import { SystemRoles } from '@/client/users';
import {
  RedirectPaths,
  RedirectPathsEnum,
  templateBuilderLegend,
} from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import { BeePluginPageContainer } from '@/components/bee-plugin';
import { LandingPageTemplateInformationForm } from '@/components/landing-page-templates/forms';
import { ClipboardContainer } from '@/components/templates';
import { useSavePageTemplate } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUserState } from '@/store/features/users';
import { isNotValidHandlebarsEmailTemplateCheck } from '@/system-settings';
import { DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { AbsoluteLoader, FlexContainer } from '@/ui/styled-ui';
import { BackButton } from '@/ui/styled-ui/BackButton';
import { AxiosError } from 'axios';
import { isEqual } from 'lodash';
import { MenuItem } from 'primereact/menuitem';
import { Steps } from 'primereact/steps';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import plainTemplate from './templates/plain-template.json';

const StyledMainContainer = styled(FlexContainer)`
  position: relative;
`;

const StyledFormWrapper = styled.div`
  padding: var(--default-padding);
`;

const getBreadcrumbs = (
  account: Account,
  t: TranslationFunctionType,
): MenuItem[] => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: account?.isSystem
      ? t('templates.predefined.page')
      : t('templates.custom.page'),
    url: account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.LANDING_PAGE_TEMPLATES_PREDEFINED]()
      : RedirectPaths[RedirectPathsEnum.LANDING_PAGE_TEMPLATES_CUSTOM](),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('templates.create.page'),
    url: RedirectPaths[RedirectPathsEnum.LANDING_PAGE_TEMPLATES_CREATE](),
    template: AppBreadCrumbTemplate,
  },
];

const initialValues = (location: Location) =>
  ({
    name: '',
    captureCredentials:
      location?.state?.landingPageTemplate?.captureCredentials || false,
    redirectUrl: location?.state?.landingPageTemplate?.redirectUrl || '',
    redirectPage:
      location?.state?.landingPageTemplate?.redirectPage || undefined,
    redirectType: location?.state?.landingPageTemplate?.redirectPage
      ? 'template'
      : 'url',
    active: location?.state?.landingPageTemplate?.active ?? true,
    usedForAiGeneratedCampaignTemplate: false,
  } as LandingPageTemplateFormState);

export const LandingPageTemplatesCreatePage: React.FC = () => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { user } = useAppSelector(selectCurrentUserState);
  const navigate = useNavigate();
  const toast = useToast();
  const saveTemplate = useSavePageTemplate();
  const location = useLocation();
  const campaignId = location?.state?.campaignId;

  const { setDialogData } = useContext(DialogContext);

  const [showLoader, setShowLoader] = useState(false);

  const steps = [
    {
      label: t('templates.info'),
    },
    {
      label: t('templates.structure'),
    },
  ];
  // Set the initial step
  const [activeStep, setActiveStep] = useState(0);
  const [blockNextStep, setBlockNextStep] = useState(true);
  const [isTemplateChanged, setIsTemplateChanged] = useState(false);

  // this is used to prevent the form from reinitializing on every change made
  const [initialFormValues, setInitialFormValues] =
    useState<LandingPageTemplateFormState>(initialValues(location));
  // Set form initial values
  const [formValues, setFormValues] = useState<LandingPageTemplateFormState>(
    initialValues(location),
  );

  // Set initial template for Bee
  const [initialTemplate, setInitialTemplate] = useState(
    location?.state?.landingPageTemplate?.editor ?? (plainTemplate as object),
  );

  // Unblock step 2 if the form is valid
  const toggleStepBlocked = (isValid: boolean) => {
    setBlockNextStep(!isValid);
  };

  const showExitPrompt = () => {
    // initial values of the form
    const formValuesToCompare = initialValues(location);
    return isTemplateChanged || !isEqual(formValuesToCompare, formValues);
  };

  // Track changes to the template
  const handleChangeTemplate = (page: any) => {
    setInitialTemplate(page);
    setIsTemplateChanged(true);
  };

  const triggerNextStep = async () =>
    await handleNextStep(formValues.name, currentAccount as Account, 1);

  // Check if a template with this name already exists
  const handleNextStep = async (
    name: string,
    account: Account,
    step: number,
  ) => {
    const filters = account.isSystem
      ? [equal('name', name), equal('account', null)]
      : [equal('name', name), equal('account', account.id)];

    try {
      const landingPageTemplates = await client.pageTemplates.getPageTemplates({
        filters: [...filters, equal('type', PageTemplatesEnum.LANDING)],
      });

      // Check if the name is available and continue to step 2, else show an error
      if (landingPageTemplates.count) {
        toast?.error(
          t('templates.name.unavailable'),
          t('templates.name.another'),
        );
      } else {
        setActiveStep(step);
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  // Check if the url is empty, and set it to null
  const checkIfEmptyString = (str: string | null | undefined) => {
    if (str === '') {
      return null;
    }
    return str;
  };

  // Handle SAVE from Bee container
  const handleSaveLandingPageTemplate = async (page: object, html: string) => {
    try {
      if (!currentAccount) {
        return;
      }

      const isNotValid = isNotValidHandlebarsEmailTemplateCheck(html, toast);
      if (isNotValid) {
        setShowLoader(false);
        return;
      }

      // TODO: Hit the save bee plugin here, and get the page and html from it
      let redirectUrl = checkIfEmptyString(formValues.redirectUrl);
      let redirectPage: string | undefined | null = formValues.redirectPage?.id;

      if (formValues?.redirectType === 'template') redirectUrl = null;
      else redirectPage = null;

      const params = {
        type: PageTemplatesEnum.LANDING,
        name: formValues.name,
        captureCredentials: formValues.captureCredentials,
        active: formValues.active,
        usedForAiGeneratedCampaignTemplate:
          formValues.usedForAiGeneratedCampaignTemplate,
        redirectUrl,
        redirectPage,
        editor: page,
        html: html,
        // TODO: Uncomment when we integrate our own Save button
        // editor: initialTemplate || plainTemplate,
        // html: '<!DOCTYPE html></html>', // Find a way to get the html form BEE
        account: currentAccount.isSystem ? null : currentAccount.id,
      };

      setShowLoader(true);
      await saveTemplate.create(params);

      setShowLoader(false);
      toast?.success(t('toast.success'), t('templates.page.created'));

      if (!campaignId) {
        if (currentAccount.isSystem) {
          navigate(
            RedirectPaths[
              RedirectPathsEnum.LANDING_PAGE_TEMPLATES_PREDEFINED
            ](),
          );
        } else {
          navigate(
            RedirectPaths[RedirectPathsEnum.LANDING_PAGE_TEMPLATES_CUSTOM](),
          );
        }
      } else {
        navigate(RedirectPaths[RedirectPathsEnum.CAMPAIGNS_EDIT](campaignId), {
          state: {
            activeStep: 1,
            isTemplate: !!currentAccount?.isSystem,
          },
        });
      }
    } catch (e) {
      setShowLoader(false);
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleExit = () => {
    const navigateUrl =
      RedirectPaths[
        RedirectPathsEnum[
          currentAccount?.isSystem
            ? 'LANDING_PAGE_TEMPLATES_PREDEFINED'
            : 'LANDING_PAGE_TEMPLATES_CUSTOM'
        ]
      ]();

    if (!showExitPrompt()) {
      navigate(navigateUrl);

      return;
    }

    setDialogData({
      type: DialogType.CONFIRMATION,
      show: true,
      header: t('dialog.unsavedChanges'),
      message: t('dialog.unsavedChanges.confirm'),
      onAccept: () => navigate(navigateUrl),
    });
  };

  return (
    <StyledMainContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      minHeight="100%"
    >
      {showLoader && <AbsoluteLoader message={t('templates.saving')} />}
      <div id="template-header-container">
        <AppBreadCrumb model={getBreadcrumbs(currentAccount as Account, t)} />
        <h1>{t('templates.create.page')}</h1>
        <Steps
          model={steps}
          activeIndex={activeStep}
          onSelect={async (e) => {
            // Check if the name is available only when switching from step 0 to 1
            if (e.index === 1 && currentAccount) {
              await triggerNextStep();
              return;
            }

            setInitialFormValues(formValues);
            setActiveStep(e.index);
          }}
          readOnly={blockNextStep}
        />
        <ClipboardContainer
          clipboardItems={templateBuilderLegend(t, {
            includeEmailProvider:
              !!user?.role?.code &&
              [SystemRoles.SUPER_ADMIN, SystemRoles.DEVELOPER].includes(
                user.role.code,
              ),
          })}
        />
      </div>
      {campaignId && (
        <BackButton
          to={RedirectPaths[RedirectPathsEnum.CAMPAIGNS_EDIT](campaignId)}
          text={t('campaign.back')}
          className="mb-3"
          options={{
            state: {
              activeStep: 1,
              isTemplate: !!currentAccount?.isSystem,
            },
          }}
        />
      )}
      <div id="template-content-container">
        {activeStep === 0 && (
          <StyledFormWrapper>
            <LandingPageTemplateInformationForm
              initialValues={initialFormValues}
              onSubmit={async () => {
                // Check if the name is available and continue to step 2, else show an error
                if (currentAccount) {
                  await triggerNextStep();
                }
              }}
              onInputChange={setFormValues}
              onValidateForm={toggleStepBlocked}
              isSystem={currentAccount?.isSystem}
            />
          </StyledFormWrapper>
        )}
        {activeStep === 1 && (
          <BeePluginPageContainer
            template={initialTemplate || plainTemplate}
            handleChange={handleChangeTemplate}
            handleSave={handleSaveLandingPageTemplate}
          />
        )}
      </div>
      <FlexContainer id="template-footer-container" justify="space-between">
        <div>
          <AppButton
            type="outlined"
            severity="secondary"
            label={t('button.exit')}
            className="mr-3"
            onClick={handleExit}
          />
          {/* TODO: Uncomment when we find a way to get the html onChange */}
          {/* <AppButton
            severity="secondary"
            label={t('button.save')}
            onClick={handleSaveLandingPageTemplate}
            isDisabled={blockNextStep}
          /> */}
        </div>

        <div>
          {activeStep > 0 && (
            <AppButton
              severity="secondary"
              label={t('button.back')}
              icon="pi pi-arrow-left"
              iconPos="left"
              onClick={() => {
                setInitialFormValues(formValues);
                setActiveStep(0);
              }}
              className="mr-3"
            />
          )}
          <AppButton
            severity="secondary"
            label={t('button.next')}
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={async () => {
              // Check if the name is available and continue to step 2, else show an error
              if (currentAccount) {
                await triggerNextStep();
              }
            }}
            isDisabled={blockNextStep || activeStep === 1}
          />
        </div>
      </FlexContainer>
    </StyledMainContainer>
  );
};
